import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { STOCK_REASONS } from "./shared";

const MANUAL = ["RESTOCK", "REFILLED_EMPTIES", "WALKIN_SALE", "EMPTIES_COLLECTED", "CORRECTION"];
const selectCls = "mt-1 h-10 w-full border border-slate-300 rounded-sm px-3 text-sm bg-white text-slate-900";

export const StockAdjustDialog = ({ station, open, onOpenChange, onSaved }) => {
  const rows = station?.stock_rows || [];
  const [form, setForm] = useState({ cylinder_type_id: "", reason: "RESTOCK", quantity: "1", full_delta: "0", empty_delta: "0", notes: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm((f) => ({ ...f, cylinder_type_id: rows[0]?.cylinder_type_id || "", quantity: "1", notes: "" })); }, [open]); // eslint-disable-line
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/stations/${station.id}/stock`, {
        cylinder_type_id: form.cylinder_type_id, reason: form.reason, quantity: Number(form.quantity) || 0,
        full_delta: Number(form.full_delta) || 0, empty_delta: Number(form.empty_delta) || 0, notes: form.notes,
      });
      toast.success("Stock updated");
      onOpenChange(false); onSaved?.();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Stock update failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md w-[calc(100%-2rem)] text-slate-900" data-testid="stock-adjust-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Stock movement · {station?.name}</DialogTitle>
          <DialogDescription>Record cylinders coming in or going out of this station.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Cylinder type</Label>
            <select value={form.cylinder_type_id} onChange={set("cylinder_type_id")} className={selectCls} data-testid="stock-adjust-type">
              {rows.map((r) => <option key={r.cylinder_type_id} value={r.cylinder_type_id}>{r.cylinder_type} (full {r.full} · empty {r.empty})</option>)}
            </select></div>
          <div><Label>Movement</Label>
            <select value={form.reason} onChange={set("reason")} className={selectCls} data-testid="stock-adjust-reason">
              {MANUAL.map((r) => <option key={r} value={r}>{STOCK_REASONS[r]}</option>)}
            </select></div>
          {form.reason === "CORRECTION" ? (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Full ±</Label><Input type="number" value={form.full_delta} onChange={set("full_delta")} className="rounded-sm mt-1" data-testid="stock-adjust-full-delta" /></div>
              <div><Label>Empty ±</Label><Input type="number" value={form.empty_delta} onChange={set("empty_delta")} className="rounded-sm mt-1" data-testid="stock-adjust-empty-delta" /></div>
            </div>
          ) : (
            <div><Label>Quantity</Label><Input type="number" min="1" value={form.quantity} onChange={set("quantity")} className="rounded-sm mt-1" data-testid="stock-adjust-qty" /></div>
          )}
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={set("notes")} className="rounded-sm mt-1" data-testid="stock-adjust-notes" /></div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-sm bg-brand-600 hover:bg-brand-700" data-testid="stock-adjust-save">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ReconcileForm = ({ station, onDone, dark = false }) => {
  const rows = station?.stock_rows || [];
  const [counts, setCounts] = useState({});
  const [notes, setNotes] = useState("");
  const [apply, setApply] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const val = (id, k) => counts[id]?.[k] ?? "";
  const setVal = (id, k) => (e) => setCounts({ ...counts, [id]: { ...counts[id], [k]: e.target.value } });
  const txt = dark ? "text-slate-200" : "text-slate-700";

  const submit = async (e) => {
    e.preventDefault();
    const payload = rows.map((r) => ({
      cylinder_type_id: r.cylinder_type_id,
      full_counted: Number(val(r.cylinder_type_id, "full") === "" ? r.full : val(r.cylinder_type_id, "full")),
      empty_counted: Number(val(r.cylinder_type_id, "empty") === "" ? r.empty : val(r.cylinder_type_id, "empty")),
    }));
    setSaving(true);
    try {
      const { data } = await api.post(`/stations/${station.id}/reconciliations`, { counts: payload, notes, apply_adjustment: apply });
      setResult(data); setCounts({}); setNotes("");
      toast.success(data.has_variance ? "Count saved — variance recorded" : "Count saved — stock matches");
      onDone?.();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="reconcile-form">
      <table className={`w-full text-sm ${txt}`}>
        <thead className="text-[10px] uppercase tracking-widest text-slate-500">
          <tr><th className="text-left py-1">Cylinder</th><th className="text-left py-1">Full (system → counted)</th><th className="text-left py-1">Empty (system → counted)</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.cylinder_type_id} className="border-t border-slate-200/20">
              <td className="py-2 font-medium">{r.cylinder_type}</td>
              {["full", "empty"].map((k) => (
                <td key={k} className="py-2 pr-2">
                  <div className="flex items-center gap-2">
                    <span className="tabular-nums w-6 text-slate-500">{r[k]}</span>
                    <Input type="number" min="0" placeholder={String(r[k])} value={val(r.cylinder_type_id, k)} onChange={setVal(r.cylinder_type_id, k)}
                      className="rounded-sm h-9 w-20 text-slate-900" data-testid={`reconcile-${k}-${r.cylinder_type.replace(/\s+/g, "")}`} />
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <Textarea rows={2} placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm text-slate-900" data-testid="reconcile-notes" />
      <label className={`flex items-center gap-2 text-xs ${txt}`}>
        <input type="checkbox" checked={apply} onChange={(e) => setApply(e.target.checked)} data-testid="reconcile-apply" />
        Update system stock to the counted numbers
      </label>
      <Button type="submit" disabled={saving} className="rounded-sm bg-brand-600 hover:bg-brand-700 w-full" data-testid="reconcile-submit">{saving ? "Saving…" : "Submit end-of-day count"}</Button>
      {result && (
        <div className={`text-xs rounded-sm border p-3 ${result.has_variance ? "border-amber-300 bg-amber-50 text-amber-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`} data-testid="reconcile-result">
          {result.has_variance ? "Variance found: " : "All counts match. "}
          {result.rows.filter((r) => r.full_variance || r.empty_variance).map((r) => `${r.cylinder_type} full ${r.full_variance >= 0 ? "+" : ""}${r.full_variance}, empty ${r.empty_variance >= 0 ? "+" : ""}${r.empty_variance}`).join("; ")}
          <div className="mt-1 text-[11px] opacity-80">Today: {result.released_today} released · {result.empties_returned_today} empties returned · {result.walkin_sales_today} walk-in sales</div>
        </div>
      )}
    </form>
  );
};
